import { Component, DestroyRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidatorFn } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UboHelpComponent } from '@shared/dialog-contents/ubo-help/ubo-help.component';
import { SELECT_INVALID_SOLDO_FIELD, collectFormErrors, scrollToFirstResult } from '@shared/formHelpers';
import { Contact, ContactRole, CountryMetadata, addContactRole, removeContactRole } from '@shared/models/data-model';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { CountryService } from '@shared/services/country.service';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { LoadingScreenService } from '@shared/services/loading-screen.service';
import { ModalDialogService } from '@shared/services/modal-dialog.service';
import { RegistrationService } from '@shared/services/registration.service';
import { REGISTRATION_STEP_ID } from '@shared/services/steps.service';
import { NGXLogger } from 'ngx-logger';
import { BeneficialOwnerFormComponent, BeneficialOwnerFormGroup } from './beneficial-owner-form/beneficial-owner-form.component';


interface BeneficialOwnersFormGroup {
    readOnlyContacts: FormArray<FormGroup<BeneficialOwnerFormGroup>>;
    contacts: FormArray<FormGroup<BeneficialOwnerFormGroup>>;
}

@Component({
    selector: 'app-beneficial-owners',
    templateUrl: './beneficial-owners.component.html',
    styleUrls: ['./beneficial-owners.component.scss']
})
export class BeneficialOwnersComponent implements OnInit {
    private readonly stepId = REGISTRATION_STEP_ID.UBO_DETAILS;
    contactsDTO: Contact[] = [];
    beneficialOwnersForm: FormGroup<BeneficialOwnersFormGroup> = this.fb.group({
        readOnlyContacts: new FormArray<FormGroup<BeneficialOwnerFormGroup>>([]),
        contacts: new FormArray<FormGroup<BeneficialOwnerFormGroup>>([])
    });

    directorsAndAdmins: Contact[];
    translatedCountries: CountryMetadata[] = [];

    loading: boolean = false;
    @ViewChildren(BeneficialOwnerFormComponent) boForms: QueryList<BeneficialOwnerFormComponent>;

    constructor(
        private readonly logger: NGXLogger,
        private readonly registrationService: RegistrationService,
        private readonly fb: FormBuilder,
        private readonly modalDialogService: ModalDialogService,
        private readonly amplitudeService: AmplitudeService,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dynatrace: DynatraceHelperService,
        private readonly countryService: CountryService,
        private readonly translate: TranslateService,
        private readonly loadingScreenService: LoadingScreenService,
        private readonly destroyRef: DestroyRef
    ) { }

    ngOnInit() {
        const allContacts: Contact[] = this.route.snapshot.data.contacts;
        this.contactsDTO = allContacts;
        this.directorsAndAdmins = allContacts.filter(c => c.roles.some(r => r == ContactRole.Admin || r == ContactRole.LegalRepresentative));

        const beneficialOwners = allContacts.filter(c => c.roles.some(r => r == ContactRole.UltimateBeneficialOwner));

        for (const contact of beneficialOwners) {
            const boFormGroup = BeneficialOwnerFormComponent.createBeneficialOwnerFormGroupForContact(this.fb, contact);
            if (contact.roles.some(r => r == ContactRole.Admin || r == ContactRole.LegalRepresentative)) {
                this.beneficialOwnersForm.controls.readOnlyContacts.push(boFormGroup);
            } else {
                this.beneficialOwnersForm.controls.contacts.push(boFormGroup);
            }
        }

        this.translatedCountries = this.route.snapshot.data.countryList;
        this.translate.onLangChange.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(async () => {
            this.loadingScreenService.show('LOADING_SCREEN.COUNTRY_TRANSLATION');
            this.translatedCountries = await this.countryService.getCurrentCountries();
            this.loadingScreenService.hide();
        });
    }

    get totalBeneficialOwners(): number {
        return this.beneficialOwnersForm.controls.readOnlyContacts.controls.length + this.beneficialOwnersForm.controls.contacts.controls.length;
    }

    onDirectorOrAdminSelected(contactId: string, selected: boolean): void {
        const contact = this.directorsAndAdmins.find(c => c.id == contactId);

        if (selected) {
            const boFormFroup = BeneficialOwnerFormComponent.createBeneficialOwnerFormGroupForContact(this.fb, contact);
            this.beneficialOwnersForm.controls.readOnlyContacts.push(boFormFroup);
        } else {
            const position = this.beneficialOwnersForm.controls.readOnlyContacts.controls.findIndex(g => g.controls.contactId.value === contactId);
            this.beneficialOwnersForm.controls.readOnlyContacts.removeAt(position);
        }
    }

    isInBeneficialOwnerForm(contactId: string): boolean {
        return this.beneficialOwnersForm.controls.readOnlyContacts.controls.some(uboForm => uboForm.controls.contactId.value == contactId);
    }

    canAddMore(): boolean {
        return this.totalBeneficialOwners < 3;
    }

    addBeneficialOwner(): void {
        const newBoFormGroup = BeneficialOwnerFormComponent.createEmptyBeneficialOwnerFormGroup(this.fb);
        this.beneficialOwnersForm.controls.contacts.push(newBoFormGroup);
        setTimeout(() => {
            this.boForms.last?.firstNameInput.nativeElement.focus();
        });
    }

    removeBeneficialOwnerAt(index: number): void {
        this.beneficialOwnersForm.controls.contacts.removeAt(index);
    }

    openUBOHelpDialog(): void {
        this.modalDialogService.showModalDialog(UboHelpComponent, {
            title: "MODAL_CONTENTS.UBO_HELP.TITLE",
            dismissLabel: "MODAL_CONTENTS.UBO_HELP.BUTTONS.DISMISS"
        });
    }

    onBoFormGroupInitialized(uboFormGroup: FormGroup) {
        const uniqueEmailValidator = this.uniqueEmailValidatorFactory(uboFormGroup);
        uboFormGroup.controls.email.addValidators(uniqueEmailValidator);
        uboFormGroup.controls.email.valueChanges.subscribe(() => {
            this.updateOtherEmailErrorStatus(uboFormGroup);
        });
    }

    uniqueEmailValidatorFactory(uboFormGroup: FormGroup): ValidatorFn {
        return (control: AbstractControl) => {
            const controlEmail: string = control.value;

            if (this.contactsDTO.some(c => uboFormGroup.controls.contactId.value !== c.id
                && c.email?.toLowerCase() === controlEmail?.toLowerCase())) {
                return { noDuplicatedEmail: true };
            }
            if (this.beneficialOwnersForm.controls.contacts.controls.some(uboControl => uboControl !== uboFormGroup
                && uboControl.value?.email?.toLowerCase() === controlEmail?.toLowerCase())) {
                return { noDuplicatedEmail: true };
            }
            return null;
        }
    }

    updateOtherEmailErrorStatus(uboFormGroup: FormGroup) {
        for (let uboForm of this.beneficialOwnersForm.controls.contacts.controls) {
            if (uboForm !== uboFormGroup) {
                let emailControl = uboForm.controls.email;
                emailControl.updateValueAndValidity({ emitEvent: false });
            }
        }
    }

    goToPrevious() {
        const backActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.BACK_TO_PREVIOUS);
        this.router.navigate([this.registrationService.getPreviousStep()]).then(() => {
            this.dynatrace.leaveAction(backActionId);
        });
    }

    onSubmit(): boolean {
        this.beneficialOwnersForm.markAllAsTouched();

        if (this.totalBeneficialOwners == 0 || !this.beneficialOwnersForm.valid) {
            this.logger.debug("FORM NOT VALID OR EMPTY!");
            this.amplitudeService.trackFormSubmissionRejected(
                this.route.snapshot.data['pageName'],
                collectFormErrors(this.beneficialOwnersForm, this.totalBeneficialOwners == 0 ? { beneficialOwners: 'atLeastOne' } : {})
            );
            scrollToFirstResult(SELECT_INVALID_SOLDO_FIELD);
            return false;
        }

        this.logger.debug("VALID!", this.beneficialOwnersForm.value);

        const submitActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.SUBMIT_FORM);

        const contactsToBeSaved: Contact[] = [];
        const importantContacts = this.contactsDTO
            .filter(c => c.roles.some(r => [ContactRole.Admin, ContactRole.Applicant, ContactRole.LegalRepresentative].includes(r)));
        for (let contact of importantContacts) {
            if (this.isInBeneficialOwnerForm(contact.id)) {
                addContactRole(contact, ContactRole.UltimateBeneficialOwner);
            } else {
                removeContactRole(contact, ContactRole.UltimateBeneficialOwner);
            }
            contactsToBeSaved.push(contact);
        }

        for (let uboFormContact of this.beneficialOwnersForm.value.contacts) {
            contactsToBeSaved.push({
                id: uboFormContact.contactId,
                firstName: uboFormContact.firstName,
                lastName: uboFormContact.lastName,
                citizenshipCountryISO: uboFormContact.nationality,
                address: {
                    countryISO: uboFormContact.countryOfResidence
                },
                email: uboFormContact.email,
                roles: [ContactRole.UltimateBeneficialOwner]
            });
        }

        this.logger.debug("Full list of operations", contactsToBeSaved);

        const usageStats = {
            totalBeneficialOwners: contactsToBeSaved.filter(c => c.roles.includes(ContactRole.UltimateBeneficialOwner)).length,
            directorIsBeneficialOwner: contactsToBeSaved.some(c => {
                return c.roles.includes(ContactRole.LegalRepresentative) && c.roles.includes(ContactRole.UltimateBeneficialOwner);
            }),
            totalAdminBeneficialOwners: contactsToBeSaved.filter(c => {
                return c.roles.includes(ContactRole.Admin) && c.roles.includes(ContactRole.UltimateBeneficialOwner);
            }).length,
            totalExtraBeneficialOwners: this.beneficialOwnersForm.value.contacts.length
        };

        this.loading = true;
        this.registrationService.saveAllContacts(contactsToBeSaved).then(() => {

            this.amplitudeService.trackBeneficialOwnersCreated(
                usageStats.totalBeneficialOwners,
                usageStats.directorIsBeneficialOwner,
                usageStats.totalAdminBeneficialOwners,
                usageStats.totalExtraBeneficialOwners);

            this.registrationService.completeStep(REGISTRATION_STEP_ID.UBO_DETAILS);
            this.router.navigate([this.registrationService.getNextStep()]).then(() => {
                this.dynatrace.leaveAction(submitActionId);
            });

        }, (err) => {
            this.logger.trace("FATAL: ", err);
            this.dynatrace.leaveAction(submitActionId);
        });

        return true;
    }
}
